/* DESCONEXIÓN — capa interactiva: cursor, botones magnéticos, inclinación 3D, antetítulos que se
   descifran, frase que se ilumina al bajar y notificaciones del hero que se silencian.
   Lo que es movimiento solo corre con html.dx-anim; lo que es interacción funciona siempre. */
(() => {
  const html = document.documentElement;
  const movimiento = html.classList.contains('dx-anim') && 'IntersectionObserver' in window;
  const punteroFino = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const limitar = (v, min, max) => Math.max(min, Math.min(max, v));

  /* ---------- Cursor: aro que sigue al ratón con algo de inercia ---------- */
  function cursor() {
    if (!punteroFino || document.querySelector('.dx-cursor')) return;
    const aro = document.createElement('div');
    aro.className = 'dx-cursor';
    aro.setAttribute('aria-hidden', 'true');
    document.body.append(aro);

    let x = 0;
    let y = 0;
    let ax = 0;
    let ay = 0;
    let corriendo = false;
    const inercia = movimiento ? 0.2 : 1;

    const pintar = () => {
      ax += (x - ax) * inercia;
      ay += (y - ay) * inercia;
      aro.style.translate = `${ax.toFixed(1)}px ${ay.toFixed(1)}px`;
      if (Math.abs(x - ax) > 0.1 || Math.abs(y - ay) > 0.1) {
        requestAnimationFrame(pintar);
      } else {
        corriendo = false;
      }
    };

    window.addEventListener(
      'pointermove',
      (e) => {
        if (e.pointerType !== 'mouse') return;
        x = e.clientX;
        y = e.clientY;
        if (!html.classList.contains('dx-cursor-visible')) {
          ax = x;
          ay = y;
          html.classList.add('dx-cursor-visible');
        }
        if (!corriendo) {
          corriendo = true;
          requestAnimationFrame(pintar);
        }
      },
      { passive: true }
    );

    document.addEventListener('pointerover', (e) => {
      const sobre = e.target.closest?.('a, button, summary, label, [role="button"], input[type="range"]');
      html.classList.toggle('dx-cursor-activo', Boolean(sobre));
    });
    document.addEventListener('mouseout', (e) => {
      if (!e.relatedTarget) html.classList.remove('dx-cursor-visible');
    });
    window.addEventListener('pointerdown', () => html.classList.add('dx-cursor-pulsado'));
    window.addEventListener('pointerup', () => html.classList.remove('dx-cursor-pulsado'));
  }

  /* ---------- Botones magnéticos: se acercan un poco al puntero ---------- */
  function magneticos(raiz) {
    if (!movimiento || !punteroFino) return;
    raiz.querySelectorAll('.dx-boton').forEach((boton) => {
      if (boton.dataset.dxIman) return;
      boton.dataset.dxIman = 'true';
      boton.addEventListener('pointermove', (e) => {
        const r = boton.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
        const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
        boton.style.translate = `${(dx * 14).toFixed(1)}px ${(dy * 10).toFixed(1)}px`;
      });
      boton.addEventListener('pointerleave', () => (boton.style.translate = ''));
    });
  }

  /* ---------- Inclinación 3D con reflejo que sigue al puntero ---------- */
  function inclinar(zona, objetivo, maximo) {
    if (!movimiento || !punteroFino || !zona || !objetivo || objetivo.dataset.dxInclina) return;
    objetivo.dataset.dxInclina = 'true';
    objetivo.classList.add('dx-inclinable');
    let rx = 0;
    let ry = 0;
    let tx = 0;
    let ty = 0;
    let corriendo = false;

    const bucle = () => {
      rx += (tx - rx) * 0.12;
      ry += (ty - ry) * 0.12;
      objetivo.style.transform = `perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;
      if (Math.abs(tx - rx) > 0.02 || Math.abs(ty - ry) > 0.02) {
        requestAnimationFrame(bucle);
      } else {
        corriendo = false;
        if (!tx && !ty) objetivo.style.transform = '';
      }
    };
    const arrancar = () => {
      if (corriendo) return;
      corriendo = true;
      requestAnimationFrame(bucle);
    };

    zona.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      const r = objetivo.getBoundingClientRect();
      const px = limitar((e.clientX - r.left) / r.width, 0, 1);
      const py = limitar((e.clientY - r.top) / r.height, 0, 1);
      ty = (px - 0.5) * 2 * maximo;
      tx = (0.5 - py) * 2 * maximo;
      objetivo.style.setProperty('--dx-gx', `${(px * 100).toFixed(1)}%`);
      objetivo.style.setProperty('--dx-gy', `${(py * 100).toFixed(1)}%`);
      objetivo.classList.add('dx-brillo');
      arrancar();
    });
    zona.addEventListener('pointerleave', () => {
      tx = 0;
      ty = 0;
      objetivo.classList.remove('dx-brillo');
      arrancar();
    });
  }

  /* ---------- Antetítulos que se descifran al entrar en pantalla ---------- */
  const SIGNOS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+/<>';

  function descifrar(el) {
    const final = el.textContent;
    if (!final.trim()) return;
    el.setAttribute('aria-label', final);
    const duracion = Math.min(1100, 400 + final.length * 22);
    const inicio = performance.now();
    const paso = (t) => {
      const p = Math.min(1, (t - inicio) / duracion);
      const fijas = Math.floor(p * final.length);
      let texto = '';
      for (let i = 0; i < final.length; i++) {
        const c = final[i];
        texto += i < fijas || /\s/.test(c) ? c : SIGNOS[(Math.random() * SIGNOS.length) | 0];
      }
      el.textContent = texto;
      if (p < 1) {
        requestAnimationFrame(paso);
      } else {
        el.textContent = final;
        el.removeAttribute('aria-label');
      }
    };
    requestAnimationFrame(paso);
  }

  const observadorCifrado = movimiento
    ? new IntersectionObserver(
        (entradas) => {
          entradas.forEach((e) => {
            if (!e.isIntersecting) return;
            observadorCifrado.unobserve(e.target);
            descifrar(e.target);
          });
        },
        { threshold: 0.8 }
      )
    : null;

  /* ---------- Frase de cierre que se ilumina palabra a palabra con el scroll ---------- */
  const frasesLuz = new Set();
  let luzPendiente = false;

  function partirEnLuces(el) {
    const luces = [];
    const recorrer = (padre) => {
      for (const nodo of [...padre.childNodes]) {
        if (nodo.nodeType === Node.ELEMENT_NODE) {
          recorrer(nodo);
          continue;
        }
        if (nodo.nodeType !== Node.TEXT_NODE || !nodo.textContent.trim()) continue;
        const frag = document.createDocumentFragment();
        nodo.textContent.split(/(\s+)/).forEach((trozo) => {
          if (!trozo) return;
          if (/^\s+$/.test(trozo)) {
            frag.append(trozo);
            return;
          }
          const luz = document.createElement('span');
          luz.className = 'dx-luz';
          luz.textContent = trozo;
          luces.push(luz);
          frag.append(luz);
        });
        nodo.replaceWith(frag);
      }
    };
    recorrer(el);
    return luces;
  }

  function iluminar() {
    luzPendiente = false;
    const alto = window.innerHeight;
    frasesLuz.forEach((frase) => {
      const r = frase.el.getBoundingClientRect();
      const progreso = limitar((alto * 0.9 - r.top) / (alto * 0.45), 0, 1);
      const encendidas = Math.round(progreso * frase.luces.length);
      frase.luces.forEach((luz, i) => luz.classList.toggle('is-on', i < encendidas));
    });
  }

  const pedirLuz = () => {
    if (luzPendiente || !frasesLuz.size) return;
    luzPendiente = true;
    requestAnimationFrame(iluminar);
  };

  const observadorLuz = movimiento
    ? new IntersectionObserver((entradas) => {
        entradas.forEach((e) => {
          const frase = e.target._dxFrase;
          if (e.isIntersecting) frasesLuz.add(frase);
          else frasesLuz.delete(frase);
        });
        pedirLuz();
      })
    : null;

  if (movimiento) {
    window.addEventListener('scroll', pedirLuz, { passive: true });
    window.addEventListener('resize', pedirLuz, { passive: true });
  }

  /* ---------- Notificaciones del hero: se silencian al tocarlas ---------- */
  function notificaciones(raiz) {
    raiz.querySelectorAll('[data-dx-notis]').forEach((caja) => {
      if (caja.dataset.dxListo) return;
      caja.dataset.dxListo = 'true';
      const fin = caja.querySelector('.dx-notis__fin');

      caja.addEventListener('click', (e) => {
        const noti = e.target.closest('.dx-noti');
        if (!noti || noti.classList.contains('is-fuera')) return;
        noti.classList.add('is-fuera');
        const siguiente = [...caja.querySelectorAll('.dx-noti:not(.is-fuera)')][0];
        const quitar = () => {
          noti.hidden = true;
          if (siguiente) {
            if (document.activeElement === noti || document.activeElement === document.body) siguiente.focus();
            return;
          }
          caja.classList.add('is-vacia');
          if (fin) fin.textContent = fin.dataset.texto || '';
        };
        if (movimiento) setTimeout(quitar, 450);
        else quitar();
      });
    });
  }

  /* ---------- Arranque ---------- */
  function iniciar(raiz = document) {
    magneticos(raiz);
    notificaciones(raiz);

    raiz.querySelectorAll('.dx-prod').forEach((fila) => inclinar(fila, fila.querySelector('.dx-prod__img'), 6));
    raiz.querySelectorAll('.dx-hero__video').forEach((zona) => inclinar(zona, zona.querySelector('.dx-video__marco'), 8));

    if (movimiento) {
      raiz.querySelectorAll('.dx-antetitulo').forEach((el) => {
        if (el.closest('.dx-hero') || el.dataset.dxCifra) return;
        el.dataset.dxCifra = 'true';
        observadorCifrado.observe(el);
      });
      raiz.querySelectorAll('.dx-problema__cierre').forEach((el) => {
        if (el._dxFrase) return;
        el._dxFrase = { el, luces: partirEnLuces(el) };
        observadorLuz.observe(el);
      });
    }
  }

  const empezar = () => {
    cursor();
    iniciar();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', empezar);
  } else {
    empezar();
  }

  document.addEventListener('shopify:section:load', (e) => iniciar(e.target));
})();
